"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { VirtuosoGrid } from "react-virtuoso";
import {
  Heart,
  Flame,
  SlidersHorizontal,
  Sparkles,
  SearchX,
  Loader2,
  ServerCrash,
} from "lucide-react";
import { useSearch, type SearchResponse } from "@/hooks/use-search";
import { useFavorites } from "@/hooks/use-favorites";
import { useSession } from "@/hooks/use-session";
import {
  parseBrowserState,
  serializeBrowserState,
  countActiveFilters,
  DEFAULT_BROWSER_STATE,
  type BrowserState,
} from "@/lib/search/url-state";
import { SearchInput } from "./search-input";
import { FiltersPanel } from "./filters-panel";
import { ActiveFilters } from "./active-filters";
import { SortSelect } from "./sort-select";
import { EssenceCard, type BrowseScope } from "./essence-card";
import { EssenceModal } from "./essence-modal";
import { EssenceGridSkeleton } from "./essence-browser-skeleton";
import { Button } from "@/components/ui/button";
import { Segmented } from "@/components/ui/segmented";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const DEBOUNCE_MS = 250;

interface EssenceBrowserProps {
  scope?: BrowseScope;
  /** First page of default results, rendered on the server for crawlers and fast first paint. */
  initialResults?: SearchResponse;
}

const SCOPE_COPY: Record<BrowseScope, { title: string; subtitle: string }> = {
  browse: {
    title: "Browse",
    subtitle: "Search and filter the whole Essence library.",
  },
  favorites: { title: "Favorites", subtitle: "Essences you have saved." },
  popular: { title: "Popular", subtitle: "What readers open and save most." },
};

export function EssenceBrowser({ scope = "browse", initialResults }: EssenceBrowserProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const defaults = useMemo<BrowserState>(
    () => ({
      ...DEFAULT_BROWSER_STATE,
      sort: scope === "popular" ? "most_viewed" : DEFAULT_BROWSER_STATE.sort,
    }),
    [scope]
  );

  const state = useMemo(() => parseBrowserState(searchParams, defaults), [searchParams, defaults]);

  const updateUrl = useCallback(
    (next: Partial<BrowserState>, replace = false) => {
      const newState = { ...state, ...next };
      const qs = serializeBrowserState(newState, defaults);
      const url = qs ? `${pathname}?${qs}` : pathname;
      if (replace) {
        router.replace(url, { scroll: false });
      } else {
        router.push(url, { scroll: false });
      }
    },
    [pathname, router, state, defaults]
  );

  const { session, isPending: sessionPending } = useSession();
  const { favorites, isLoading: favoritesLoading } = useFavorites();

  const isFavoritesScope = scope === "favorites";
  const favoriteIds = useMemo(
    () => (isFavoritesScope ? [...favorites].sort() : undefined),
    [favorites, isFavoritesScope]
  );
  const favoritesReady = !isFavoritesScope || (!!session && !favoritesLoading);
  const searchEnabled = !isFavoritesScope || (favoritesReady && favorites.length > 0);

  // The server renders the first page for the untouched browse state only.
  const isDefaultState =
    scope === "browse" &&
    state.query === "" &&
    state.fields.length === 0 &&
    state.filters.length === 0 &&
    state.sort === defaults.sort;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useSearch({
    query: state.query,
    fields: state.fields,
    filters: state.filters,
    sort: state.sort,
    ids: favoriteIds,
    enabled: searchEnabled,
    initialPage: isDefaultState ? initialResults : undefined,
  });

  const hits = useMemo(() => data?.pages.flatMap((p) => p.hits) ?? [], [data]);
  const total = data?.pages[0]?.found ?? 0;
  const facetCounts = data?.pages[0]?.facet_counts;
  const hasQuery = state.query.trim().length > 0;
  const activeCount = countActiveFilters(state);
  const isSearching = isFetching && !isFetchingNextPage && !isLoading;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Close the sheet on route/state change from inside it is handled by the footer button.
  useEffect(() => {
    if (!filtersOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFiltersOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filtersOpen]);

  const copy = SCOPE_COPY[scope];
  // Signed-out favorites has nothing to search; show only the sign-in prompt.
  const showControls = !isFavoritesScope || sessionPending || !!session;
  const metric = state.sort === "most_favorited" ? "favorites" : "views";

  const placeholder = isFavoritesScope
    ? "Search your favorites…"
    : total > 0 && !hasQuery && activeCount === 0
      ? `Search ${total.toLocaleString()} Essences…`
      : "Search Essences…";

  const countLabel = (() => {
    if (isLoading && searchEnabled) return "Searching…";
    const n = total.toLocaleString();
    const noun = total === 1 ? "Essence" : "Essences";
    if (hasQuery) return `${n} ${total === 1 ? "result" : "results"} for “${state.query.trim()}”`;
    return `${n} ${noun}`;
  })();

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-4 sm:px-6 lg:py-8">
      <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
        {/* Desktop filter rail */}
        <aside className="hidden w-[272px] shrink-0 lg:block">
          <div className="sticky top-[4.5rem] max-h-[calc(100dvh-5.5rem)] overflow-y-auto pr-1 no-scrollbar">
            <FiltersPanel state={state} onChange={updateUrl} facetCounts={facetCounts} />
          </div>
        </aside>

        {/* Main column */}
        <div className="min-w-0 flex-1">
          <div
            className={cn(
              "mb-4 items-end justify-between gap-4",
              scope === "browse" ? "hidden lg:flex" : "flex"
            )}
          >
            <div>
              <h1 className="font-display text-[22px] font-semibold tracking-[-0.01em] text-ink">
                {copy.title}
              </h1>
              <p className="mt-0.5 text-[13.5px] text-ink-3">{copy.subtitle}</p>
            </div>
          </div>

          {/* Search row: sticky on small screens so the lens stays in reach */}
          {showControls && (
            <div className="sticky top-14 z-30 -mx-4 bg-[rgb(8_7_15/0.85)] px-4 py-3 backdrop-blur-xl sm:-mx-6 sm:px-6 lg:static lg:mx-0 lg:bg-transparent lg:px-0 lg:py-0 lg:backdrop-blur-none">
              <div className="flex items-center gap-2">
                <SearchInput
                  value={state.query}
                  onChange={(query) => updateUrl({ query }, true)}
                  fields={state.fields}
                  onFieldsChange={(fields) => updateUrl({ fields })}
                  debounceMs={DEBOUNCE_MS}
                  placeholder={placeholder}
                  isSearching={isSearching}
                  className="min-w-0 flex-1"
                />

                {/* Mobile filters sheet */}
                <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                  <SheetTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="relative size-12 shrink-0 rounded-full lg:hidden"
                      aria-label={activeCount > 0 ? `Filters, ${activeCount} active` : "Filters"}
                    >
                      <SlidersHorizontal className="size-[18px]" />
                      {activeCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1 font-mono text-[10.5px] text-void">
                          {activeCount}
                        </span>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="h-[88dvh]" showCloseButton={false}>
                    <SheetHeader className="flex-row items-center justify-between">
                      <div>
                        <SheetTitle>Filters</SheetTitle>
                        <SheetDescription>
                          Narrow the library by category, trait, keyword or author.
                        </SheetDescription>
                      </div>
                      {activeCount > 0 && (
                        <button
                          type="button"
                          onClick={() => updateUrl({ filters: [] })}
                          className="shrink-0 text-[13px] font-medium text-accent"
                        >
                          Clear all
                        </button>
                      )}
                    </SheetHeader>
                    <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">
                      <FiltersPanel
                        state={state}
                        onChange={updateUrl}
                        facetCounts={facetCounts}
                        hideHeader
                        variant="sheet"
                      />
                    </div>
                    <SheetFooter>
                      <Button
                        variant="primary"
                        size="lg"
                        className="w-full"
                        onClick={() => setFiltersOpen(false)}
                      >
                        {isFetching ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          `Show ${total.toLocaleString()} ${total === 1 ? "Essence" : "Essences"}`
                        )}
                      </Button>
                    </SheetFooter>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          )}

          {/* Status line */}
          {showControls && (
            <div className="mt-4 flex flex-col gap-3 lg:mt-5">
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <p className="font-mono text-[12.5px] tabular text-ink-3" aria-live="polite">
                  {searchEnabled || !isFavoritesScope ? countLabel : ""}
                </p>
                <div className="flex items-center gap-2">
                  {scope === "popular" ? (
                    <Segmented
                      aria-label="Popularity metric"
                      value={state.sort === "most_favorited" ? "most_favorited" : "most_viewed"}
                      onChange={(sort) => updateUrl({ sort })}
                      options={[
                        {
                          value: "most_viewed",
                          label: (
                            <>
                              <Flame className="size-3.5" /> Most viewed
                            </>
                          ),
                        },
                        {
                          value: "most_favorited",
                          label: (
                            <>
                              <Heart className="size-3.5" /> Most favorited
                            </>
                          ),
                        },
                      ]}
                    />
                  ) : (
                    <SortSelect value={state.sort} onChange={(sort) => updateUrl({ sort })} />
                  )}
                </div>
              </div>
              <ActiveFilters state={state} onChange={updateUrl} />
            </div>
          )}

          {/* Results */}
          <div className="mt-5">
            {isFavoritesScope && !sessionPending && !session ? (
              <EmptyState
                icon={<Heart className="size-5" />}
                title="Sign in to see your favorites"
                body="Favorites are saved to your account so they follow you across devices."
                action={
                  <Button variant="primary" asChild>
                    <Link href="/login">Log in</Link>
                  </Button>
                }
              />
            ) : isFavoritesScope && favoritesReady && favorites.length === 0 ? (
              <EmptyState
                icon={<Sparkles className="size-5" />}
                title="No favorites yet"
                body="Tap the heart on any Essence to keep it here."
                action={
                  <Button variant="accent" asChild>
                    <Link href="/">Browse the library</Link>
                  </Button>
                }
              />
            ) : isError ? (
              <EmptyState
                icon={<ServerCrash className="size-5" />}
                title="Search is unavailable right now"
                body="The search service did not respond. Try again in a moment."
                action={
                  <Button variant="accent" onClick={() => refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : isLoading || (isFavoritesScope && !favoritesReady) ? (
              <EssenceGridSkeleton />
            ) : hits.length === 0 ? (
              <EmptyState
                icon={<SearchX className="size-5" />}
                title="No Essences match"
                body={
                  hasQuery && activeCount > 0
                    ? "Try a broader search term, or remove a filter."
                    : hasQuery
                      ? "Check the spelling, or try fewer words."
                      : "Remove a filter to widen the results."
                }
                action={
                  <Button
                    variant="accent"
                    onClick={() => updateUrl({ query: "", filters: [], fields: [] })}
                  >
                    Clear search and filters
                  </Button>
                }
              />
            ) : (
              <div className={cn("transition-opacity duration-200", isSearching && "opacity-70")}>
                <VirtuosoGrid
                  data={hits}
                  useWindowScroll
                  overscan={600}
                  // Virtuoso corrects scroll position itself when measured row
                  // heights change; browser scroll anchoring double-compensates.
                  listClassName="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 [overflow-anchor:none]"
                  // Always seed the first render with the loaded page. Without
                  // this, a client-side mount (direct link with URL state) can
                  // deadlock: zero rows rendered → zero list height → empty
                  // render range forever.
                  initialItemCount={Math.min(hits.length, 24)}
                  endReached={() => {
                    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
                  }}
                  // Fixed-height rows: uniform card heights make row measurement
                  // exact, eliminating the scroll-correction feedback loops that
                  // variable-height cards caused.
                  itemClassName="flex min-w-0 h-[420px]"
                  computeItemKey={(_index, hit) => hit.document.id}
                  itemContent={(_index, hit) => (
                    <EssenceCard
                      hit={hit}
                      scope={scope}
                      metric={metric}
                      hasQuery={hasQuery}
                      onSelect={() => setSelectedId(hit.document.essence_id)}
                    />
                  )}
                  components={{
                    Footer: () => (
                      <div className="flex items-center justify-center py-8 text-[12.5px] text-ink-3">
                        {isFetchingNextPage ? (
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="size-4 animate-spin text-accent" /> Loading more
                          </span>
                        ) : !hasNextPage && hits.length > 0 ? (
                          <span className="font-mono">— end of results —</span>
                        ) : null}
                      </div>
                    ),
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedId && <EssenceModal id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="surface mx-auto flex max-w-md flex-col items-center rounded-2xl px-6 py-12 text-center animate-fade-in">
      <span className="flex size-11 items-center justify-center rounded-full border border-[rgb(167_139_250/0.3)] bg-accent-tint text-accent">
        {icon}
      </span>
      <h2 className="mt-4 font-display text-[17px] font-semibold text-ink">{title}</h2>
      <p className="mt-1.5 max-w-xs text-[13.5px] leading-relaxed text-ink-3">{body}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
